import { atom } from 'jotai';
import { loadable } from 'jotai/utils';
import request from 'lib/request';
import { documentsConfig } from 'pages/map-query/datasets/documents.config';
import type { DatasetFacets } from 'pages/map-query/types';
import type { AddressArea, AddressMatchStats, ClusteringRun } from './types';

// GET a JSON endpoint, surfacing a failure as a thrown error so the wrapping
// `loadable` atom exposes it as `hasError` (request.get resolves to `{ error }`
// on failure rather than rejecting).
async function fetchJson<T>(
  url: string,
  abortController?: AbortController
): Promise<T> {
  const json = await request.get(url, abortController);
  if (json && typeof json === 'object' && 'error' in json) {
    throw new Error(String((json as { error: unknown }).error));
  }
  return json as T;
}

// ── Clustering runs (hub table) ─────────────────────────────────────────────

// Bump to refetch; the base atom reads it as a dependency. The combined atom's
// setter increments it, and background polling triggers it only when the data
// actually changed (fingerprint diff) so the table doesn't flicker on unchanged
// payloads.
const clusteringRunsRefreshAtom = atom(0);

export async function fetchClusteringRuns(): Promise<ClusteringRun[]> {
  const json = await fetchJson<{ runs?: ClusteringRun[] }>(
    '/admin/bulk_dedupe/clustering_runs'
  );
  return json.runs ?? [];
}

const clusteringRunsBaseAtom = atom((get) => {
  get(clusteringRunsRefreshAtom);
  return fetchClusteringRuns();
});

const clusteringRunsLoadableAtom = loadable(clusteringRunsBaseAtom);

// read: loadable state ({ state: 'loading' | 'hasData' | 'hasError' });
// write: refetch (bump the refresh counter).
export const clusteringRunsAtom = atom(
  (get) => get(clusteringRunsLoadableAtom),
  (_get, set) => set(clusteringRunsRefreshAtom, (c) => c + 1)
);

// Cheap change-detector for polling: only the fields the hub renders and that
// change while a run generates/executes.
export function clusteringRunsFingerprint(runs: ClusteringRun[]): string {
  return runs
    .map(
      (r) =>
        `${r.id}:${r.status}:${r.total}:${r.counts.in_scope ?? 0}:${
          r.counts.partially_excluded ?? 0
        }:${r.counts.combined ?? 0}:${r.counts.failed ?? 0}:${
          r.counts.reversed ?? 0
        }`
    )
    .join('|');
}

// ── Address-run launch modal ────────────────────────────────────────────────

// A stable cache key for a set of areas. match_stats is a POST (the areas ride
// in the JSON body), so this is only an identity for the fetched figures — not
// a URL — and the serialized areas are enough to tell one scope from another.
export function areasQuery(areas: AddressArea[]): string {
  return JSON.stringify(areas);
}

// POSTed (not GET) so a many-county pick can't overflow Puma's URI limit.
// All-locations is explicit; areas omitted.
export async function fetchMatchStats(
  areas: AddressArea[],
  abortController?: AbortController
): Promise<AddressMatchStats> {
  const res = await request.post<AddressMatchStats, { error: string }>(
    '/admin/bulk_dedupe/match_stats',
    areas.length ? { areas } : { allLocations: true },
    abortController ?? new AbortController()
  );
  if (res.type === 'success') return res.data;
  if (res.type === 'error')
    throw new Error(res.data?.error ?? 'Request failed');
  throw new Error(res.message);
}

// The state and county document buckets, from the facet request the map-query
// documents filter makes for its own State / County tree.
export async function fetchLocationFacets(
  abortController: AbortController
): Promise<DatasetFacets> {
  const res = await request.post<{ facets?: DatasetFacets }, { error: string }>(
    documentsConfig.searchEndpoint,
    {
      ...documentsConfig.defaultPayload,
      page: 1,
      pageSize: 1,
      response_config: { include_facets: true },
    },
    abortController
  );
  // A failure must surface (empty `{}` silently rendered an empty tree with
  // Run Batch still enabled, which then ran every location).
  if (res?.type === 'success') return res.data?.facets ?? {};
  if (res?.type === 'error')
    throw new Error(res.data?.error ?? 'Failed to load locations');
  throw new Error(res?.message ?? 'Failed to load locations');
}
